import React, { useCallback, useEffect, useRef, useState } from 'react';
import ActionMenu from '@/components/actionmenu';
import { Alert, Button, Empty, Pagination, Skeleton, Spin, message } from 'antd';
import { EyeInvisibleOutlined, HeartOutlined, UndoOutlined } from '@ant-design/icons';
import { fetchNearbyPosts, fetchRecommendedPosts, sendRecommendationFeedback } from '@/apis/posts';
import { getRequestErrorMessage } from '@/utils/requestError';
import PullToRefresh from '@/components/pulltorefresh';
import PostFilters from '@/components/postfilters';
import PostFeedActions from '@/components/postfeedactions';
import ConfirmAction from '@/components/confirmaction';
import NearbyLocationSettings from './NearbyLocationSettings';
import SavedRecommendations from './SavedRecommendations';
import { useBrowseState } from '@/utils/browseState';
import './index.scss';

const Discovery = ({ mode, isMobile, navigation, CardComponent, onPost }) => {
  const [items, setItems] = useBrowseState(`discovery:${mode}:items`, []);
  const [pagination, setPagination, restored] = useBrowseState(`discovery:${mode}:pagination`, null);
  const [page, setPage] = useBrowseState(`discovery:${mode}:page`, 1);
  const [position, setPosition] = useBrowseState(`discovery:${mode}:position`, null);
  const [loading, setLoading] = useState(false); const [error, setError] = useState(''); const [busy, setBusy] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [dismissed, setDismissed] = useState(null);
  const [filters, setFilters] = useBrowseState(`discovery:${mode}:filters`, {});
  const [loadedQuery, setLoadedQuery] = useBrowseState(`discovery:${mode}:loaded-query`, null);
  const restoredPosition = useRef(position).current;
  const queryKey = JSON.stringify({ mode, page, position, filters });
  const resumeQuery = useRef(restored && pagination && loadedQuery === queryKey ? queryKey : null);
  const version = useRef(0); const feedbackLock = useRef(false);
  const [locating, setLocating] = useState(false);
  const [locationInitialized, setLocationInitialized] = useState(Boolean(restoredPosition));
  const locationAttempted = useRef(false);
  const feedbackContext = useRef(0);
  useEffect(() => {
    setDismissed(null); setBusy(null);
    return () => { feedbackContext.current += 1; };
  }, [mode]);
  const load = useCallback(async () => {
    const requestId = ++version.current;
    if (mode === 'nearby' && !position) { setItems([]); setPagination(null); setLoading(false); setError(''); return; }
    setLoading(true); setError(''); setErrorStatus(null);
    if (mode === 'nearby') { setItems([]); setPagination(null); }
    try {
      const params = { page, pageSize: 10, ...filters, ...(mode === 'nearby' ? position : {}) };
      const result = await (mode === 'nearby' ? fetchNearbyPosts(params) : fetchRecommendedPosts(params));
      if (requestId === version.current) {
        setItems(result.data); setPagination(result.pagination);
        setLoadedQuery(JSON.stringify({ mode, page, position, filters }));
      }
    } catch (err) {
      const text = getRequestErrorMessage(err, '帖子加载失败');
      if (requestId === version.current) {
        setError(text);
        setErrorStatus(err.response?.status || null);
        if (mode === 'nearby') { setItems([]); setPagination(null); }
      }
      throw new Error(text);
    }
    finally { if (requestId === version.current) setLoading(false); }
  }, [mode, page, position, filters, setItems, setPagination, setLoadedQuery]);
  useEffect(() => {
    if (resumeQuery.current !== queryKey) {
      resumeQuery.current = null;
      load().catch(() => {});
    }
    return () => { version.current += 1; };
  }, [load, queryKey]);
  const feedback = async (id, action) => {
    if (feedbackLock.current) return false; feedbackLock.current = true; setBusy(id);
    const context = feedbackContext.current;
    try {
      await sendRecommendationFeedback(id, action);
      if (context !== feedbackContext.current) return false;
      if (action === 'dismissed') {
        setDismissed(items.find(item => item.id === id));
        setItems(current => current.filter(item => item.id !== id));
      }
      if (action === 'clear' && dismissed?.id === id) {
        setDismissed(null);
        void load().catch(() => {});
      }
      message.success(action === 'interested' ? (items.find(item => item.id === id)?.is_recruiting ? '已记录感兴趣；加入招募仍需在详情页申请' : '已记录感兴趣') : action === 'clear' ? '已撤销推荐反馈' : '已隐藏这条推荐');
      return true;
    } catch (err) { if (context === feedbackContext.current) message.error(getRequestErrorMessage(err, '反馈失败')); return false; }
    finally { feedbackLock.current = false; if (context === feedbackContext.current) setBusy(null); }
  };
  const clearNearby = () => {
    version.current += 1; setPosition(null); setPage(1);
    setItems([]); setPagination(null); setError(''); setLoading(false);
    setLocationInitialized(true);
  };
  const handleLocationBusy = value => {
    setLocating(value);
    if (value) locationAttempted.current = true;
    else if (locationAttempted.current) setLocationInitialized(true);
  };
  const nearbyWaiting = mode === 'nearby' && (loading || !pagination);
  const nearbyPending = loading || Boolean(position) || locating || !locationInitialized;
  const nearbyMessage = position ? '正在查找附近招募…'
    : !locationInitialized ? '正在获取当前位置，准备查找附近招募…'
    : locating ? '正在确认查询位置…' : '请打开“位置设置”选择地点并查询附近招募';
  const recommendationTools = <SavedRecommendations filters={filters} isMobile={isMobile} />;
  const recommendedNavigation = navigation && !isMobile ? React.cloneElement(navigation, { tabBarExtraContent: recommendationTools }) : navigation;
  return <div className="discovery">
    {mode === 'recommended' && <PostFilters recommended isMobile={isMobile} navigation={recommendedNavigation} disabled={loading || busy !== null} onChange={values => { setFilters(values); setPage(1); }} />}
    {mode === 'recommended' && (isMobile || !navigation) && <div className="discovery-tools-bar" role="group" aria-label="推荐工具">{recommendationTools}</div>}
    {mode === 'recommended' && dismissed && <Alert type="info" showIcon style={{ marginBottom: 16 }} title={`已隐藏推荐：${dismissed.title}`} action={<ConfirmAction title="确认撤销隐藏？该帖子将重新参与推荐。" disabled={busy !== null} onConfirm={() => feedback(dismissed.id, 'clear')}><Button size="small" disabled={busy !== null}>撤销隐藏</Button></ConfirmAction>} />}
    {mode === 'nearby' && <NearbyLocationSettings initialPosition={restoredPosition} isMobile={isMobile} navigation={navigation} posts={!loading && !error && position ? items : []}
      renderNavigation={trigger => <PostFilters nearby isMobile={isMobile} navigation={navigation} extraActions={trigger}
        disabled={loading} onChange={values => { setFilters(values); setPage(1); }} />}
      loading={loading} onBusyChange={handleLocationBusy} onClear={clearNearby} onQuery={values => { setPosition(values); setPage(1); }} />}
    <PullToRefresh enabled={isMobile} disabled={loading || busy !== null || (mode === 'nearby' && !position)} onRefresh={load} successMessage={mode === 'nearby' ? '附近招募已刷新' : '推荐帖子已刷新'}>
      {nearbyWaiting && !error ? <div className="nearby-query-state" role="status" aria-live="polite">
        {nearbyPending && <Spin />}
        <p>{nearbyMessage}</p>
      </div> : loading ? <Skeleton active /> : error ? <Alert type="error" title={error} action={errorStatus === 409 && /学校/.test(error) ? <Button onClick={() => window.location.reload()}>重新加载页面</Button> : <Button onClick={() => load().catch(() => {})}>重试</Button>} /> : <>
        {!items.length && <Empty description={mode === 'nearby' ? (Object.keys(filters).length ? '附近暂无符合条件的招募，可调整筛选、位置或范围' : '附近暂无招募帖子，可调整位置或扩大范围') : '暂无符合条件的招募'} />}
        <div className="post-feed-list">{items.map(post => <CardComponent key={post.id} post={post} footer={mode === 'recommended' && <div className="recommendation-feedback">
          {post.recommendation_reasons?.length > 0 && <div className="recommendation-feedback-reason"><span className="recommendation-feedback-label">推荐理由</span><span>{post.recommendation_reasons.join(' · ')}</span></div>}
          <div className="recommendation-feedback-actions" aria-label="推荐反馈">
            <ActionMenu label="推荐反馈" disabled={busy !== null} buttonProps={{ size: 'small', loading: busy === post.id }} items={[
              { key: 'interested', label: '感兴趣', icon: <HeartOutlined aria-hidden="true" />, onClick: () => feedback(post.id, 'interested') },
              { key: 'dismissed', label: '不感兴趣', icon: <EyeInvisibleOutlined aria-hidden="true" />, confirm: '确认标记为不感兴趣？该帖子将从推荐列表中隐藏。', onClick: () => feedback(post.id, 'dismissed') },
              { key: 'clear', label: '撤销反馈', icon: <UndoOutlined aria-hidden="true" />, confirm: '确认撤销推荐反馈？此前标记的感兴趣或不感兴趣将被清除。', onClick: () => feedback(post.id, 'clear') },
            ]} />
          </div>
        </div>} />)}</div>
        {pagination?.total > 10 && <Pagination current={page} pageSize={10} total={pagination.total} showSizeChanger={false} disabled={loading || busy !== null} onChange={setPage} />}
      </>}
    </PullToRefresh>
    <PostFeedActions isMobile={isMobile} onPost={onPost} loading={loading}
      disabled={loading || busy !== null || (mode === 'nearby' && (!position || locating))} onRefresh={() => load().catch(() => {})} />
  </div>;
};
export default Discovery;
